import { parseSelection } from '~/fc/advancedOptionHelper'
import Job from '~/fc/Converter/job'
import { uuidv4 } from "~/fc/Helpers/FileHelpers";
import { getRangeAndRotationFromRangeString } from '../Helpers/ThumbPreviewHelper';

export default class BulkConverter {
    constructor(ctx) {
        this.context = ctx;
        this.auth = this.context.$auth;
        this.store = $nuxt.$store;
        this.info = this.store.state.info;
        this.slug = ctx.slug
        this.rotationOptionId = ctx.rotationOptionId
        this.topMarginOptionId = 'image_top_margin'
        this.leftMarginOptionId = 'image_left_margin'
        this.autoSaveToDrive = this.store.state.autoSaveToDrive
        this.list = $nuxt.$store.state.list
        this.ranges = this.store.state.thumb.ranges ? Object.entries(this.store.state.thumb.ranges).map(([key, value]) => {
            const [partition, fileId] = key.split(':')
            return { partition, fileId, range: value }
        }) : null; 

        if (this.store.state.advancedSetting.merging) {
            this.target = this.store.state.advancedSetting.merging.split('-')[this.store.state.advancedSetting.merging.split('-').length - 1]
        } else if (this.store.state.advancedSetting.defaultTarget) {
            this.target = this.store.state.advancedSetting.defaultTarget
        }

        this.options =
            this.slug &&
                this.store.state.advancedSetting.perConversionType[this.slug] &&
                this.store.state.advancedSetting.perConversionType[this.slug].list
                ? this.store.state.advancedSetting.perConversionType[this.slug].list
                : []
    }

    generateImportTasks() {
        const importTasks = {}
        const uploadTasks = {}
        const files = (this.ranges && this.ranges.length) ? this.store.state.sourceList : this.list
        files.forEach((importTask, i) => {
            if(importTask.importTaskId || importTask.import) return 
            if (importTask.file === 'url') {
                if (importTask.gtoken) {
                    importTasks['import-' + i] = {
                        operation: 'import/google-drive',
                        fileId: importTask.url,
                        gtoken: importTask.gtoken,
                        filename: importTask.name,
                        filesize: importTask.total
                    }
                } else {
                    if (importTask.name) {
                        importTasks['import-' + i] = {
                            operation: 'import/url',
                            url: importTask.url,
                            filename: importTask.name
                        }
                    } else {
                        importTasks['import-' + i] = {
                            operation: 'import/url',
                            url: importTask.url
                        }
                    }
                }
            } else {
                importTasks['import-' + i] = { operation: 'import/upload' }
                uploadTasks['import-' + i] = importTask
            }
        })

        return [importTasks, uploadTasks]
    }

    /**
     * We don't always need conversion tasks.
     * @returns 
     */
    generateConvertTasks(getTasks) {
        const convertTasks = {}
    
        this.list.forEach((importTask, i) => {
            const selections = this.store.state.advancedSetting.userData.allSelection[importTask.id]    
            const hasRotationChanges = selections && selections[this.rotationOptionId] != undefined && selections[this.rotationOptionId] != '0'
            const hasTopMarginChanges = selections && selections[this.topMarginOptionId] != undefined && selections[this.topMarginOptionId] != '0'
            const hasLeftMarginChanges = selections && selections[this.leftMarginOptionId] != undefined && selections[this.leftMarginOptionId] != '0'

            if (hasRotationChanges || hasTopMarginChanges || hasLeftMarginChanges) {
                const options = parseSelection(
                    this.options,
                    selections,
                    getTasks,
                    true
                )
                if(importTask.password) options['password'] = importTask.password

                convertTasks['convert-' + i] = {
                    operation: 'convert',
                    input: importTask.import || 'import-' + i,
                    output_format: importTask.ext,
                    options
                }
            }
        })

        return [convertTasks]
    }

    buildPdfToPdfRangeOption() {
        const rangeInputs = []
        const rangeOptions = []
        const rangeInputsMap = new Map()

        if(this.ranges && this.ranges.length) {
            this.ranges.forEach((range, index) => {              
                const fileId = range.fileId.includes('_') ? range.fileId.split('_')[0] : range.fileId
                const importFile = this.list.find(file => file.id === fileId) 
                    ? this.list.find(file => file.id === fileId)
                    : this.list.flatMap(item => item.files).find(file => file.id === fileId)
                const importTaskIndex = this.list.findIndex(file => file.id === fileId)                

                range.range.split(',').forEach(rangeString => {
                    if (rangeString === "B") {
                        // Blank page
                        rangeOptions.push({
                            index: 'B'
                        })
                    } else {
                        // Keep unique import file in the input array
                        const input = importFile.importTaskId || importFile.import || 'import-' + importTaskIndex
                        if (!rangeInputsMap.has(input)) {
                            rangeInputs.push(input)
                            rangeInputsMap.set(input, rangeInputs.length - 1)                            
                        }

                        // Extract range and rotation if present
                        const { rotation, range } = getRangeAndRotationFromRangeString(rangeString)
                        
                        // Build the object
                        rangeOptions.push({
                            index: rangeInputsMap.get(input),
                            range,
                            ...(rotation && { rotate: rotation }),
                            ...(importFile.password && { password: importFile.password }),
                        })
                    }
                })     
            })
        }

        return {
            rangeInputs,
            rangeOptions
        }
    }

    generateMergeTask (importTasks, convertTasks, getTasks) {
        const { advancedSetting } = this.store.state
        const options = parseSelection(this.options, advancedSetting.userData.allSelection.root, getTasks, true) || {}
        const target = this.target ? this.target.toLowerCase() : ''
        
        let mergeInputs = Object.keys(importTasks).map((key, index) => (convertTasks.hasOwnProperty('convert-' + index) ? ('convert-' + index) : key))
        let reloadedImports = this.list.filter(file => file.import).map(file => file.import)

        let rangeInputs = []
        if (this.ranges && this.ranges.length) {
            const option  = this.buildPdfToPdfRangeOption()
            options['pdf_to_pdf_range'] = option.rangeOptions
            rangeInputs = option.rangeInputs
            mergeInputs = []
            reloadedImports = []
        }

        return {
            operation: 'merge',
            input: [...mergeInputs, ...reloadedImports, ...rangeInputs],
            output_format: target,
            options
        }
    }

    /**
     * 
     * @param {*} callback - callback function after job starts 
     */
    async process(callback, getTasks = false) {

        /**
         * Generating the task objects
         */
        const [importTasks, uploadTasks] = this.generateImportTasks()
        const [convertTasks] = this.generateConvertTasks(getTasks)
        const exportTasks = this.generateExportTasks()
        const mergeTask = this.generateMergeTask(importTasks, convertTasks, getTasks)

        const tasks = {
            ...importTasks,
            ...convertTasks,
            merge: mergeTask,
            ...exportTasks
        }        
        
        /**
         * When we don't want to start the conversion, rather
         * just need the tasks to display in the Job Builder dialog.
         */
        if(getTasks) {
            const _tasks = {...tasks}
            _tasks.merge.input_format = this.list[0].ext
            return _tasks
        }

        const job = new Job(tasks)
        await job.processBulkFiles({ uploads: uploadTasks, jobId: uuidv4() })

        callback()
    }

    /**
     * This method generates the export tasks needed for the job
     * @returns exportTasks
     */
    generateExportTasks() {
        const exportTasks = {
            'export-url': {
                operation: "export/url",
                input: 'merge',
            }
        }
        if (this.autoSaveToDrive) exportTasks['export-google-drive'] = {
            operation: "export/google-drive",
            input: 'merge',
            token: $nuxt.$store.state.store.googleDriveTokenForUpload,
            folder: this.list[0].folder || 'root'
        }
        return exportTasks
    }
}

